/**
 * Fullscreen host for spreadsheet mode (PROD-1279 exploration).
 *
 * The same shape as `day-task-dialog.tsx` — a fullscreen `DialogContent`, an
 * `sr-only` title naming the campus and the day, and a one-day `/days` read of
 * its own rather than trusting the page's list, because a deep link may name a
 * day outside the window the page currently shows.
 *
 * It differs from that dialog in one way: it resolves EVERY delivery date
 * feeding the day, not just the unreceived ones. The combined table shows the
 * Received column for a truck that is already in — read-only — because a record
 * that omits what arrived is not the record the school prints.
 *
 * Kept as a separate host rather than a branch inside `DayTaskDialog` so that
 * removing spreadsheet mode removes a directory, not a conditional.
 *
 * PROD-1279 stage-1 feedback (B1): the mealtimes are wizard steps. The step the
 * URL names (`?mealtime=`) and the page's setter for it pass straight through
 * to the flow; the dialog itself keys on the day.
 */

import { Dialog, DialogContent, DialogTitle } from '@ordo/ui/dialog'
import { Skeleton } from '@ordo/ui/skeleton'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { Mealtime } from '@/api/cafeteria'
import { useProductionRecordDaysQuery } from '@/api/production-records'
import { singleDayRange } from '@/components/production/day-task-dialog'
import { RecordSpreadsheetFlow } from '@/components/production/spreadsheet/record-spreadsheet-flow'
import { taskContextLabel } from '@/lib/production-records/day-tasks'

export interface RecordSpreadsheetDialogProps {
  locationId: string
  /** The campus's display name — the header's second line, with the day. */
  locationName: string
  /** yyyy-mm-dd service day. */
  serviceDate: string
  /** `?mealtime=` — the step to open. Null: the first unsigned one. */
  mealtime?: Mealtime | null
  /** The user picked a step; the page REPLACES `mealtime` in the URL. */
  onSelectMealtime?: (mealtime: Mealtime) => void
  /** The user left (close control, Save for later) or finished the record. */
  onClose: () => void
  /** Amend this SIGNED record in the same flow it was filled with (one step, a reason, a re-signature). */
  amend?: { productionRecordId: string } | null
}

export function RecordSpreadsheetDialog({
  locationId,
  locationName,
  serviceDate,
  mealtime = null,
  onSelectMealtime,
  onClose,
  amend = null,
}: RecordSpreadsheetDialogProps) {
  const queryClient = useQueryClient()
  const dayQuery = useProductionRecordDaysQuery({ schoolLocationId: locationId, ...singleDayRange(serviceDate) })
  const day = dayQuery.data?.days.find((d) => d.serviceDate === serviceDate) ?? null
  const subtitle = taskContextLabel(locationName, serviceDate)

  /**
   * Every truck feeding the day. `deliveryDates` already covers the received
   * ones; `unreceivedDeliveryDates` is folded in because a day whose records
   * exist before any shipment does still has work to show. Memoized: the flow
   * keys a `useQueries` and a persist effect off this array.
   */
  const deliveryDates = useMemo(() => {
    const dates = new Set<string>([
      ...(day?.transport.deliveryDates ?? []),
      ...(day?.transport.unreceivedDeliveryDates ?? []),
    ])
    return [...dates].sort()
  }, [day])

  function close(): void {
    void queryClient.invalidateQueries({ queryKey: ['production-records'] })
    void queryClient.invalidateQueries({ queryKey: ['cafeteria'] })
    onClose()
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          close()
        }
      }}>
      <DialogContent fullscreen aria-describedby={undefined}>
        <DialogTitle className="sr-only">{`${amend ? 'Amend production record' : 'Production record'} · ${subtitle}`}</DialogTitle>
        {dayQuery.isPending ? (
          <div className="flex h-full flex-col gap-4 p-6" aria-busy="true" aria-label="Loading production record">
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        ) : (
          <RecordSpreadsheetFlow
            locationId={locationId}
            serviceDate={serviceDate}
            subtitle={subtitle}
            deliveryDates={deliveryDates}
            mealtime={mealtime}
            onSelectMealtime={onSelectMealtime}
            onExit={close}
            onComplete={close}
            amend={amend}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
